import React, { useMemo, useState } from 'react'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import numeral from 'numeral'

import Checkbox from '../../Core/Checkbox/Checkbox2'
import Table from '../Table'
import InvoiceStatus from '../InvoiceStatus'

const getValueByPath = (item, path) => path
  .split('.')
  .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), item)

const getDisplayName = (item, keys, fallback) => {
  for (const key of keys) {
    const value = getValueByPath(item, key)

    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return fallback
}

const columnHelper = createColumnHelper()

const WorkTimes = ({lockEntriesOnIssue, workTimes, loading, onSelect, selected}) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)
  const [sorting, setSorting] = useState([])

  const rowSelection = useMemo(() => {
    return Object.keys(selected || {}).reduce((acc, id) => {
      acc[id] = true
      return acc
    }, {})
  }, [selected])

  const workTimesById = useMemo(() => {
    return (Array.isArray(workTimes) ? workTimes : []).reduce((acc, workTime) => {
      acc[String(workTime.id)] = workTime
      return acc
    }, {})
  }, [workTimes])

  const handleRowSelectionChange = updater => {
    const nextRowSelection = typeof updater === 'function'
      ? updater(rowSelection)
      : updater

    const nextSelectedWorkTimes = Object.keys(nextRowSelection).reduce((acc, id) => {
      if (nextRowSelection[id]) {
        acc[id] = workTimesById[id]
      }

      return acc
    }, {})

    onSelect(nextSelectedWorkTimes)
  }

  const columns = useMemo(() => ([
    columnHelper.display({
      id: 'select',
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    }),
    columnHelper.accessor(row => moment(row.started_at).valueOf(), {
      id: 'date',
      header: t('Date'),
      cell: ({ row }) => moment(row.original.started_at).format('YYYY-MM-DD'),
      enableSorting: true,
    }),
    columnHelper.accessor(row => {
      const employeeName = getDisplayName(row, ['employee.name'], '')
      const employeeSurname = getDisplayName(row, ['employee.surname'], '')
      const fullName = `${employeeName} ${employeeSurname}`.trim()

      return fullName || row.employee_id || ''
    }, {
      id: 'employee',
      header: t('Employee'),
      cell: ({ row }) => {
        const employeeName = getDisplayName(row.original, ['employee.name'], '')
        const employeeSurname = getDisplayName(row.original, ['employee.surname'], '')
        const fullName = `${employeeName} ${employeeSurname}`.trim()

        return fullName || row.original.employee_id
      },
      enableSorting: true,
    }),
    columnHelper.accessor(row => getDisplayName(
      row,
      ['place.name', 'place_name'],
      row.place_id || '',
    ), {
      id: 'place',
      header: t('Project'),
      cell: ({ row }) => getDisplayName(
        row.original,
        ['place.name', 'place_name'],
        row.original.place_id,
      ),
      enableSorting: true,
    }),
    columnHelper.accessor(row => getDisplayName(
      row,
      ['job_type.title'],
      '-',
    ), {
      id: 'job_type',
      header: t('Job type'),
      cell: ({ row }) => getDisplayName(
        row.original,
        ['job_type.title'],
        '-',
      ),
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'in_out',
      enableSorting: false,
      header: t('In/Out'),
      cell: ({ row }) => {
        return `${row.original.start} - ${row.original.end}`
      },
    }),
    columnHelper.display({
      id: 'break',
      enableSorting: false,
      header: t('Break'),
      cell: ({ row }) => {
        const breaks = row.original.breaks.reduce((acc, b) => {
          const duration = moment.duration(moment(b.finished_at).diff(moment(b.started_at)))
          const breakMins = duration.asMinutes()
          return acc + breakMins
        }, 0)
        return `${numeral(breaks).format('0')}m`
      },
    }),
    columnHelper.display({
      id: 'hours',
      enableSorting: false,
      header: t('Hours'),
      cell: ({ row }) => {
        const workDuration = moment.duration(moment(row.original.finished_at).diff(moment(row.original.started_at)))
        const breakDuration = row.original.breaks.reduce((acc, b) => {
          const duration = moment.duration(moment(b.finished_at).diff(moment(b.started_at)))
          return acc.add(duration)
        }, moment.duration(0))

        const netDuration = moment.duration(workDuration.asMilliseconds() - breakDuration.asMilliseconds())

        const hours = Math.floor(netDuration.asHours())
        const minutes = netDuration.minutes()

        return `${hours}h ${String(minutes).padStart(2, '0')}m`
      }
    }),
    columnHelper.accessor(row => Number(row.profitability?.cost ?? 0), {
      id: 'cost',
      header: t('Cost'),
      cell: ({ row }) => `${row.original.profitability.cost} ${companyInfo?.currency_symbol}`,
      enableSorting: true,
    }),
    columnHelper.accessor('billing_status', {
      id: 'status',
      header: t('Status'),
      cell: ({ getValue, row }) => {
        const status = getValue() === 'sent' ? 'billed' : getValue()
        return <InvoiceStatus status={status} dueDate={row.original.invoice_item?.invoice?.due_date} />
      },
      enableSorting: true,
    }),
  ]), [columnHelper, companyInfo?.currency_symbol, t])

  const table = useReactTable({
    data: Array.isArray(workTimes) ? workTimes : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
    state: {
      rowSelection,
      sorting,
    },
    enableRowSelection: row => !lockEntriesOnIssue || !['sent', 'paid'].includes(row.original?.billing_status),
    onRowSelectionChange: handleRowSelectionChange,
    onSortingChange: setSorting,
  })

  return (
    <Table table={table} columns={columns} loading={loading} />
  )
}

export default WorkTimes
